import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  ClipboardList, Minus, Plus, Loader2, Save, X, CheckCircle2,
  Beer, Utensils, Package, Snowflake, Search, ArrowLeft, Camera,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ShelfPhotoDialog } from "./ShelfPhotoDialog";

const ZONES = [
  { id: "barra", label: "Barra", icon: Beer },
  { id: "cocina", label: "Cocina", icon: Utensils },
  { id: "almacen", label: "Almacén", icon: Package },
  { id: "camara", label: "Cámara", icon: Snowflake },
];

/**
 * Inventario Vivo — Fase 4
 * Pantalla táctil optimizada para tablet. El usuario cuenta físicamente cada
 * producto con +/- grandes, y al guardar el backend inyecta movimientos
 * `count_adjust` para la diferencia con el stock teórico.
 */
export function InventoryCountView({ restaurantId, onBack, defaultZone = "barra" }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [stocks, setStocks] = useState({}); // {ingredient_id: theoretical_qty}
  const [counts, setCounts] = useState({}); // {ingredient_id: {qty: number, touched: bool}}
  const [activeZone, setActiveZone] = useState(defaultZone);
  const [search, setSearch] = useState("");
  const [shelfDialogOpen, setShelfDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [ings, stocksResp] = await Promise.all([
        api.getIngredients(restaurantId),
        api.getTheoreticalStocks(restaurantId),
      ]);
      setIngredients(ings);
      setStocks(stocksResp.stocks || {});
      // Pre-fill counts with theoretical so the user starts at "match"
      const initial = {};
      ings.forEach((i) => {
        initial[i.ingredient_id] = {
          qty: Number((stocksResp.stocks || {})[i.ingredient_id] ?? i.current_stock ?? 0),
          touched: false,
        };
      });
      setCounts(initial);
    } catch (e) {
      toast.error(e.message || "Error al cargar inventario");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Simple heuristic: liquid + "bebida" categories go to barra; everything else "almacen".
  const zoneFor = (ing) => {
    const cat = (ing.category || "").toLowerCase();
    const unit = ing.unit || "";
    if (["barra", "bebidas", "bar"].some((k) => cat.includes(k))) return "barra";
    if (["cocina", "pescado", "carne", "masas"].some((k) => cat.includes(k))) return "cocina";
    if (["cámara", "camara", "refrig", "lácte", "lacte"].some((k) => cat.includes(k))) return "camara";
    if (["ml", "cl", "l", "L", "dl"].includes(unit)) return "barra";
    return "almacen";
  };

  const countsByZone = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? ingredients.filter((i) => (i.name || "").toLowerCase().includes(q))
      : ingredients;
    const by = { barra: [], cocina: [], almacen: [], camara: [] };
    filtered.forEach((ing) => {
      const z = zoneFor(ing);
      (by[z] ?? by.almacen).push(ing);
    });
    // Sort each zone alphabetically
    Object.values(by).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, "es")));
    return by;
  }, [ingredients, search]);

  const touchedDeltas = useMemo(() => {
    return ingredients
      .map((ing) => {
        const c = counts[ing.ingredient_id];
        if (!c || !c.touched) return null;
        const theoretical = Number(stocks[ing.ingredient_id] ?? ing.current_stock ?? 0);
        const delta = Number((c.qty - theoretical).toFixed(4));
        if (delta === 0) return null;
        return {
          ing,
          delta,
          counted_qty: c.qty,
          value_impact: delta * Number(ing.cost_per_unit || 0),
        };
      })
      .filter(Boolean);
  }, [counts, ingredients, stocks]);

  const totalImpact = touchedDeltas.reduce((s, d) => s + d.value_impact, 0);

  const setQty = (id, qty) => {
    setCounts((prev) => ({
      ...prev,
      [id]: { qty: Math.max(0, Number(qty) || 0), touched: true },
    }));
  };
  const bump = (id, step) => {
    const cur = counts[id]?.qty ?? 0;
    setQty(id, Math.max(0, +(cur + step).toFixed(4)));
  };

  const handleSave = async () => {
    if (touchedDeltas.length === 0) {
      toast.info("Nada que guardar — no hay diferencias");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        zone: activeZone,
        items: touchedDeltas.map((d) => ({
          ingredient_id: d.ing.ingredient_id,
          counted_qty: d.counted_qty,
          zone: zoneFor(d.ing),
        })),
      };
      const r = await api.submitCountSession(restaurantId, payload);
      toast.success(`${r.movements} ajuste(s) registrados`, {
        description: `Impacto: ${r.value_impact >= 0 ? "+" : ""}${r.value_impact.toFixed(2)} €`,
      });
      await load();
    } catch (e) {
      toast.error(e.message || "Error al guardar el conteo");
    } finally {
      setSaving(false);
    }
  };

  const applyShelfDetection = (detectedItems) => {
    let applied = 0;
    setCounts((prev) => {
      const next = { ...prev };
      for (const d of detectedItems) {
        if (!d.ingredient_id) continue;
        const ing = ingredients.find((i) => i.ingredient_id === d.ingredient_id);
        if (!ing) continue;
        // For liquids, each bottle = 1L filled at avg_fill_pct → convert to base unit.
        const unit = ing.unit || "";
        const fill = Math.max(0, Math.min(1, (d.avg_fill_pct || 100) / 100));
        const bottles = Math.max(0, d.count || 0);
        let qtyInBase;
        if (unit === "l" || unit === "L") qtyInBase = bottles * fill * 1;
        else if (unit === "ml") qtyInBase = bottles * fill * 1000;
        else if (unit === "cl") qtyInBase = bottles * fill * 100;
        else if (unit === "dl") qtyInBase = bottles * fill * 10;
        else qtyInBase = bottles; // non-liquid → treat as discrete units
        next[d.ingredient_id] = { qty: +qtyInBase.toFixed(4), touched: true };
        applied += 1;
      }
      return next;
    });
    if (applied > 0) {
      toast.success(`${applied} producto(s) actualizados con la IA`, {
        description: "Revisa y pulsa Guardar conteo para confirmar",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="inventory-count-view">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3 sticky top-0 bg-background z-10 py-2">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="count-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Volver
          </Button>
        )}
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Contar inventario</h2>
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10" data-testid="count-search-input"
          />
        </div>
        <div className="flex-1" />
        <Button
          variant="outline" size="sm" onClick={() => setShelfDialogOpen(true)}
          className="gap-1.5" data-testid="count-shelf-photo-btn"
        >
          <Camera className="w-4 h-4" /> Foto IA
        </Button>
        {touchedDeltas.length > 0 && (
          <Badge variant="secondary" className="text-xs gap-1" data-testid="count-pending-badge">
            {touchedDeltas.length} pendiente(s) · {totalImpact >= 0 ? "+" : ""}{totalImpact.toFixed(2)} €
          </Badge>
        )}
        <Button
          variant="outline" size="sm" onClick={() => load()} disabled={saving}
          data-testid="count-reset-btn"
        >
          <X className="w-4 h-4 mr-1" /> Descartar
        </Button>
        <Button
          onClick={handleSave} disabled={saving || touchedDeltas.length === 0}
          className="gap-1.5" data-testid="count-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar conteo
        </Button>
      </div>

      <Tabs value={activeZone} onValueChange={setActiveZone}>
        <TabsList className="grid grid-cols-4 h-auto" data-testid="count-zone-tabs">
          {ZONES.map((z) => {
            const Icon = z.icon;
            const count = countsByZone[z.id]?.length || 0;
            return (
              <TabsTrigger
                key={z.id} value={z.id} className="flex-col gap-1 py-2"
                data-testid={`count-zone-${z.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex items-center gap-1.5">
                  {z.label} <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">{count}</Badge>
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {ZONES.map((z) => (
          <TabsContent key={z.id} value={z.id} className="mt-4">
            {(countsByZone[z.id] || []).length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  Sin productos en esta zona.
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(countsByZone[z.id] || []).map((ing) => {
                  const theoretical = Number(stocks[ing.ingredient_id] ?? ing.current_stock ?? 0);
                  const current = counts[ing.ingredient_id]?.qty ?? 0;
                  const touched = counts[ing.ingredient_id]?.touched;
                  const delta = +(current - theoretical).toFixed(4);
                  const bumpStep = ing.unit === "units" ? 1 : ing.unit === "l" || ing.unit === "L" ? 0.25 : 1;
                  return (
                    <Card
                      key={ing.ingredient_id}
                      className={`${touched && delta !== 0 ? "border-primary/60 bg-primary/5" : ""}`}
                      data-testid={`count-card-${ing.ingredient_id}`}
                    >
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{ing.name}</p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                              Teórico: {theoretical.toLocaleString("es-ES", { maximumFractionDigits: 2 })} {ing.unit}
                            </p>
                          </div>
                          {touched && delta !== 0 && (
                            <Badge
                              variant={delta > 0 ? "default" : "destructive"}
                              className="text-[10px] tabular-nums"
                              data-testid={`count-delta-${ing.ingredient_id}`}
                            >
                              {delta > 0 ? "+" : ""}{delta} {ing.unit}
                            </Badge>
                          )}
                          {touched && delta === 0 && (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button
                            type="button" size="icon" variant="outline"
                            className="h-10 w-10 flex-shrink-0"
                            onClick={() => bump(ing.ingredient_id, -bumpStep)}
                            data-testid={`count-minus-${ing.ingredient_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number" min="0" step="0.01" inputMode="decimal"
                            value={current}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setQty(ing.ingredient_id, e.target.value)}
                            className="h-10 text-center text-base font-mono tabular-nums"
                            data-testid={`count-input-${ing.ingredient_id}`}
                          />
                          <span className="text-xs text-muted-foreground w-12 flex-shrink-0">{ing.unit}</span>
                          <Button
                            type="button" size="icon" variant="outline"
                            className="h-10 w-10 flex-shrink-0"
                            onClick={() => bump(ing.ingredient_id, bumpStep)}
                            data-testid={`count-plus-${ing.ingredient_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <ShelfPhotoDialog
        open={shelfDialogOpen}
        onOpenChange={setShelfDialogOpen}
        restaurantId={restaurantId}
        zone={activeZone}
        onApply={applyShelfDetection}
      />
    </div>
  );
}

export default InventoryCountView;
